import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Heart, Phone, MapPin, Clock } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <>
      {/* Top Info Bar */}
      <div className="bg-deep-forest text-white py-2 px-4 hidden md:block">
        <div className="max-w-7xl mx-auto flex justify-between text-sm">
          <div className="flex items-center space-x-6">
            <span className="flex items-center gap-2">
              <MapPin size={14} />
              4006 Airport Road, Duluth, MN 55811
            </span>
            <span className="flex items-center gap-2">
              <Phone size={14} />
              218-722-5341
            </span>
            <span className="flex items-center gap-2">
              <Clock size={14} />
              Tue-Fri: 12-6pm, Sat-Sun: 12-4pm
            </span>
          </div>
          <div>
            <Link to="/donate" className="text-warm-coral hover:text-warm-coral/80 font-semibold flex items-center gap-2">
              <Heart size={14} fill="currentColor" />
              Donate Now
            </Link>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-white rounded-full p-2 shadow-md">
                <img 
                  src="/logo.png" 
                  alt="Animal Allies Logo" 
                  className="h-8 w-8 object-contain"
                />
              </div>
              <div>
                <h1 className="font-display text-2xl text-deep-forest">Animal Allies</h1>
                <p className="text-xs text-warm-gray">Bringing Pets and People Together</p>
              </div>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <div className="relative group">
                <Link to="/adopt" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors py-2">
                  Adopt
                </Link>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/adopt/available" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Available Pets</Link>
                  <Link to="/adopt/process" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Adoption Process</Link>
                  <Link to="/adopt/fees" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Adoption Fees</Link>
                  <Link to="/adopt/special-programs" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Special Programs</Link>
                </div>
              </div>

              <div className="relative group">
                <Link to="/services" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors py-2">
                  Services
                </Link>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/services/medical" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Medical Services</Link>
                  <Link to="/services/pet-services" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Pet Services</Link>
                  <Link to="/services/lost-found" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Lost & Found</Link>
                  <Link to="/services/surrender" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Surrender/Rehoming</Link>
                </div>
              </div>

              <div className="relative group">
                <Link to="/get-involved" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors py-2">
                  Get Involved
                </Link>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/volunteer" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Volunteer</Link>
                  <Link to="/foster" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Foster</Link>
                  <Link to="/donate" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Donate</Link>
                  <Link to="/events" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Events</Link>
                </div>
              </div>

              <div className="relative group">
                <Link to="/programs" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors py-2">
                  Programs
                </Link>
                <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <Link to="/programs/youth" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Youth Programs</Link>
                  <Link to="/programs/education" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Education</Link>
                  <Link to="/programs/camps" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Summer Camps</Link>
                  <Link to="/programs/birthday" className="block px-4 py-3 text-sm text-deep-forest hover:bg-cream-white">Birthday Parties</Link>
                </div>
              </div>

              <Link to="/resources" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors">
                Resources
              </Link>

              <Link to="/about" className="font-ui font-medium text-deep-forest hover:text-warm-coral transition-colors">
                About
              </Link>

              <Link to="/adopt" className="bg-warm-coral text-white px-6 py-3 rounded-full font-ui font-semibold hover:bg-warm-coral/90 transition-colors">
                Find Your Friend
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="lg:hidden text-deep-forest hover:text-warm-coral transition-colors"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-2 space-y-1">
              <Link to="/adopt" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">Adopt</Link>
              <Link to="/services" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">Services</Link>
              <Link to="/get-involved" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">Get Involved</Link>
              <Link to="/programs" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">Programs</Link>
              <Link to="/resources" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">Resources</Link>
              <Link to="/about" className="block py-3 text-deep-forest hover:text-warm-coral font-medium">About</Link>
              <Link to="/adopt" className="block w-full bg-warm-coral text-white px-6 py-3 rounded-full font-semibold text-center mt-4">
                Find Your Friend
              </Link>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Header;